/* assets/of-kit-upgrade-core.js (added 2026-08-05, hanger kit upgrade v1;
 * revised 2026-08-05 for the add-on recast, see snippets/of-kit-upgrade.liquid;
 * revised again same day for the second "add" lane -- base kits with no
 * -HA sibling, where accepting adds the MAMH-1 hanger as its own line;
 * revised again 2026-08-06 for PDP cart-awareness: the checkbox row now
 * checks whether the MAMH-1 hanger is ALREADY in the cart -- loose, from
 * an earlier accept, or bought outright -- and swaps to an informational
 * state instead of silently offering an add the flyout would later
 * refuse. See pdpRowState/hangerVariantId/alreadyInCartSavingCents.)
 *
 * Pure helpers behind the Foredom hanger kit upgrade feature. No DOM, no
 * network, no side effects at load. Dual exported (window.OFKitUpgradeCore
 * in the browser, module.exports in node) exactly like of-brands-core.js
 * and of-help-index-core.js, so the money math and the offer-gate logic
 * are covered by tests/kit-upgrade-core.test.mjs instead of only living
 * inside the DOM controller.
 *
 * TWO LANES, one gate function each side reuses regardless of which:
 *   - "swap": a real sibling PRODUCT exists; accepting swaps the cart
 *     line to it. entry.sibling carries its handle/variantId/price.
 *   - "add": no sibling product; accepting adds the MAMH-1 hanger as its
 *     own SECOND line, qty 1. entry.addOn carries the hanger's handle/
 *     variantId/price. entry.launchPriceCents is the anticipated price
 *     after the not-yet-created BXGY discount goes live; entry.
 *     discountLive gates the "this isn't live yet" note.
 * isAddMode(entry) is the one place that reads entry.mode; every other
 * gate function below takes plain variant ids and cart snapshots, so the
 * SAME shouldShowOffer/shouldShowCartOffer/cartHasVariant logic serves
 * both lanes -- callers just pass entry.sibling.variantId or
 * entry.addOn.variantId as the "companion" variant, whichever the lane has.
 *
 * CONSUMER: assets/of-kit-upgrade.js, which owns the DOM (the PDP add-on
 * checkbox, the post-add flyout card, and the cart-line card) and asks
 * this file every question that has a right answer:
 *   - the PDP add-on's default state (initialChoice) -- unchecked, the
 *     page's own product, not the sibling/add-on
 *   - which lane an entry is in (isAddMode)
 *   - which cents figure feeds the pill/subline math, per lane
 *     (pricingCents: the swap's live deltaCents, or the add lane's
 *     hardcoded launchPriceCents)
 *   - what the "NN% off with this kit" pill says (computePercentOff)
 *   - did the shopper just add MORE of the base kit variant, an ADD event
 *     (baseQtyIncreased), which gates the post-add flyout
 *   - is the base kit variant currently sitting in the cart at all, a
 *     STATE check (cartHasVariant), which gates the cart-line card
 *   - given all of that plus this session's dismiss/accept state, should
 *     an offer show at all (shouldShowOffer for the flyout's add-event
 *     gate, shouldShowCartOffer for the cart-line's state gate)
 *   - what was actually charged for a just-added line, real cart data,
 *     not a display constant (lineFinalPrice) -- what the post-accept
 *     confirmation states, so the add lane never claims $17.92 was
 *     charged before the BXGY discount genuinely exists
 *
 * NO DISCOUNT LOGIC LIVES HERE OR ANYWHERE IN THIS FEATURE. The swap lane
 * switches the buyer to a different existing product/variant at its
 * existing price; the add lane adds an existing product at its existing
 * price. computePercentOff and pricingCents are display math only, never
 * applied to any real price. The add lane's BXGY discount is a separate,
 * not-yet-created merchant-configured automatic discount; this feature
 * never creates, edits, or simulates one.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) root.OFKitUpgradeCore = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  // The PDP add-on checkbox starts UNCHECKED: the page's own product
  // (the base kit the shopper landed on) is what add-to-cart submits
  // unless they act. Checking it swaps in the sibling kit under the hood.
  var DEFAULT_CHOICE = 'base';

  function initialChoice() {
    return DEFAULT_CHOICE;
  }

  function formatMoney(cents) {
    var n = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n);
    } catch (_) {
      return '$' + n.toFixed(2);
    }
  }

  // sibling price minus base price, in cents. Both prices come from live
  // Liquid resolution (all_products[handle]), never hardcoded, so this is
  // always the CURRENT delta even if either product's price changes.
  function computeDelta(basePriceCents, siblingPriceCents) {
    return (Number(siblingPriceCents) || 0) - (Number(basePriceCents) || 0);
  }

  // How much of the hanger's standalone price the kit delta represents,
  // inverted and rounded to the pill's whole-percent "NN% off" copy.
  // Returns null when standaloneCents is not usable (guards divide by zero).
  function computePercentOff(deltaCents, standaloneCents) {
    var standalone = Number(standaloneCents) || 0;
    if (standalone <= 0) return null;
    var delta = Number(deltaCents) || 0;
    var pctOfStandalone = (delta / standalone) * 100;
    var pctOff = Math.round(100 - pctOfStandalone);
    if (pctOff < 0) pctOff = 0;
    if (pctOff > 100) pctOff = 100;
    return pctOff;
  }

  // Total quantity of a given variant id across every cart line (a variant
  // can legitimately appear on more than one line, e.g. different line
  // item properties), 0 for an empty/missing/malformed cart.
  function lineQty(cart, variantId) {
    if (!cart || !Array.isArray(cart.items) || !variantId) return 0;
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (it && Number(it.variant_id) === Number(variantId)) total += Number(it.quantity) || 0;
    }
    return total;
  }

  function cartHasVariant(cart, variantId) {
    return lineQty(cart, variantId) > 0;
  }

  // Total quantity across a LIST of variant ids -- the whole-product count
  // for a multi-variant base like the K.2200-2 matrix (11 handpiece
  // variants), where any single variantId check would miss the other ten.
  // Accepts a lone id too so callers can pass whichever the entry carries.
  function productQty(cart, variantIds) {
    if (!Array.isArray(variantIds)) return lineQty(cart, variantIds);
    var total = 0;
    for (var i = 0; i < variantIds.length; i++) total += lineQty(cart, variantIds[i]);
    return total;
  }

  // Array-aware counterpart of baseQtyIncreased below: true when the
  // product's TOTAL cart quantity (summed across all its variants) went
  // up between the two snapshots. A shopper adding the H.28 variant of a
  // matrix must register as a base add even when the entry's single
  // variantId points at the H.8 default.
  function productQtyIncreased(beforeCart, afterCart, variantIds) {
    return productQty(afterCart, variantIds) > productQty(beforeCart, variantIds);
  }

  // How many hangers short of one-per-kit the cart is: kits in cart minus
  // hangers in cart, floored at 0. This is the quantity every add-lane
  // accept path should add, and (> 0) is the add-lane cart card's show
  // condition -- the cart itself is the source of truth, so an accepted
  // offer hides because the hanger is PRESENT, not because a flag said so.
  function hangerDeficit(cart, baseVariantIds, hangerVarId) {
    var deficit = productQty(cart, baseVariantIds) - lineQty(cart, hangerVarId);
    return deficit > 0 ? deficit : 0;
  }

  // The per-unit price actually charged for a variant, read from a real
  // cart snapshot: final_price (post-discount) when Shopify sends one,
  // else the plain line price. Null if the variant isn't in the cart.
  // This is what a post-accept confirmation should ever state as "what
  // you paid" -- never a display constant like launchPriceCents, which
  // may not match reality until the add lane's BXGY discount is live.
  function lineFinalPrice(cart, variantId) {
    if (!cart || !Array.isArray(cart.items) || !variantId) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (it && Number(it.variant_id) === Number(variantId)) {
        return typeof it.final_price === 'number' ? it.final_price : (it.price || null);
      }
    }
    return null;
  }

  // The one place that reads entry.mode. Everything else in this file
  // takes plain variant ids so both lanes share the same gate logic.
  function isAddMode(entry) {
    return !!(entry && entry.mode === 'add');
  }

  // The MAMH-1 hanger's own variant id, resolved the same way regardless
  // of lane (entry.hanger is emitted for both "swap" and "add" entries).
  // NOT the same concept as the "companion" variant used by
  // shouldShowOffer/shouldShowCartOffer above: for "swap", the companion
  // that disqualifies THAT ask is the sibling KIT, but the hanger's own
  // variant is what the PDP row's cart-awareness (pdpRowState below)
  // needs to check -- a shopper can have a loose MAMH-1 in the cart with
  // no sibling kit line at all.
  function hangerVariantId(entry) {
    return entry && entry.hanger && entry.hanger.variantId;
  }

  // PDP row state: ALWAYS 'offer', both lanes. The add lane has been
  // unconditional since 2026-08-11 (Rhett: "It should always be an option
  // to add a hanger with a kit"); the swap lane kept a hanger-in-cart
  // 'already_in_cart' info row until 2026-08-13, when Rhett hit it on the
  // K.2230 page with a loose MAMH-1 in his cart and extended the same
  // rule to both lanes. Every kit can carry its own hanger, so the row
  // never retires. The state still feeds of_kit_upgrade_view's state
  // param; 'already_in_cart' no longer occurs in new events.
  function pdpRowState(cart, entry) {
    return 'offer';
  }

  // Which cents figure the pill/subline percent-off math is computed
  // against: the swap lane's live deltaCents (sibling price minus base
  // price), or the add lane's hardcoded launchPriceCents (there is no
  // real sibling to diff -- the discount doesn't exist yet). Same
  // downstream call either way: computePercentOff(pricingCents(entry),
  // entry.hangerStandaloneCents).
  function pricingCents(entry) {
    if (isAddMode(entry)) return entry && entry.launchPriceCents;
    return entry && entry.deltaCents;
  }

  // Generic "standalone minus the bundled price" cents math, the one
  // formula behind both of the already-in-cart row's saving claims:
  //   add lane:  hangerStandaloneCents - launchPriceCents  = $59.00
  //   swap lane: hangerStandaloneCents - deltaCents         = varies per
  //              pair ($59.00 on most, $19.14 on the K8301 pair, since
  //              that pair's delta is the largest of the five)
  function savingCents(standaloneCents, priceCents) {
    return (Number(standaloneCents) || 0) - (Number(priceCents) || 0);
  }

  // Entry-aware wrapper: reuses pricingCents so the saving always matches
  // whatever the pill/subline math is already using for this entry --
  // one formula, per-entry numbers, never hardcoded.
  function alreadyInCartSavingCents(entry) {
    return savingCents(entry && entry.hangerStandaloneCents, pricingCents(entry));
  }

  // True only when the base variant's cart quantity went UP between the two
  // snapshots. Covers both a fresh add (0 -> N) and an increment on an
  // existing line (N -> N+more); a decrement or a same-quantity mutation
  // (e.g. someone else's line changing) is not an add.
  function baseQtyIncreased(beforeCart, afterCart, baseVariantId) {
    return lineQty(afterCart, baseVariantId) > lineQty(beforeCart, baseVariantId);
  }

  // sessionStorage key for a per-base-handle, per-kind flag ('declined' or
  // 'accepted'). One namespace, so a future kind never collides. SHARED
  // across the flyout and the cart-line card on purpose: a decline or an
  // accept on either surface suppresses both for the rest of the session.
  function suppressionKey(kind, baseHandle) {
    return 'of_kit_upgrade_' + kind + '_' + String(baseHandle || '');
  }

  // The add-event gate (post-add flyout, of-nudges-core's hookCartNetwork
  // firing after a cart mutation). All conditions are ANDed: a real base
  // add just happened, the companion item is not already in the cart, and
  // the shopper has not already declined or accepted this entry this
  // session. opts.siblingVariantId is the "companion" variant either lane
  // disqualifies the offer over: the sibling kit's variant for "swap", the
  // MAMH-1 hanger's own variant for "add" (its name predates the add lane
  // but the check is identical either way -- "is this specific variant
  // already in the cart").
  function shouldShowOffer(opts) {
    opts = opts || {};
    if (opts.dismissed || opts.accepted) return false;
    var ids = Array.isArray(opts.baseVariantIds) && opts.baseVariantIds.length
      ? opts.baseVariantIds
      : [opts.baseVariantId];
    if (!productQtyIncreased(opts.beforeCart, opts.afterCart, ids)) return false;
    if (cartHasVariant(opts.afterCart, opts.siblingVariantId)) return false;
    return true;
  }

  // The state gate (cart-line card): no before/after diff, just the cart's
  // current contents. Used on the cart page itself, where the card must
  // reappear correctly on every repaint (page load, section reload,
  // another line's quantity change) rather than only once at add-time.
  // Same opts.siblingVariantId convention as shouldShowOffer above.
  //
  // opts.baseVariantIds (array) supersedes opts.baseVariantId when present,
  // so multi-variant bases count every variant. opts.perKit flips the
  // companion check from presence to quantity: show while companions in
  // cart < base units in cart (the add lane's one-hanger-per-kit rule,
  // matching the BXGY discount's per-kit application). Without perKit the
  // legacy presence check stands (swap lane: ANY sibling bundle in the
  // cart disqualifies the swap ask).
  function shouldShowCartOffer(opts) {
    opts = opts || {};
    if (opts.dismissed || opts.accepted) return false;
    var ids = Array.isArray(opts.baseVariantIds) && opts.baseVariantIds.length
      ? opts.baseVariantIds
      : [opts.baseVariantId];
    var baseQty = productQty(opts.cart, ids);
    if (baseQty < 1) return false;
    if (opts.perKit) {
      return lineQty(opts.cart, opts.siblingVariantId) < baseQty;
    }
    if (cartHasVariant(opts.cart, opts.siblingVariantId)) return false;
    return true;
  }

  // mode and state are both optional so existing callers that omit them
  // are unaffected; real call sites pass entry.mode explicitly, and the
  // PDP row's view event additionally passes state ('offer' |
  // 'already_in_cart', see pdpRowState above).
  function gaParams(surface, baseHandle, mode, state) {
    var p = { surface: surface, base: baseHandle };
    if (mode) p.mode = mode;
    if (state) p.state = state;
    return p;
  }

  return {
    DEFAULT_CHOICE: DEFAULT_CHOICE,
    initialChoice: initialChoice,
    formatMoney: formatMoney,
    computeDelta: computeDelta,
    computePercentOff: computePercentOff,
    lineQty: lineQty,
    cartHasVariant: cartHasVariant,
    productQty: productQty,
    productQtyIncreased: productQtyIncreased,
    hangerDeficit: hangerDeficit,
    lineFinalPrice: lineFinalPrice,
    baseQtyIncreased: baseQtyIncreased,
    isAddMode: isAddMode,
    pricingCents: pricingCents,
    savingCents: savingCents,
    alreadyInCartSavingCents: alreadyInCartSavingCents,
    hangerVariantId: hangerVariantId,
    pdpRowState: pdpRowState,
    suppressionKey: suppressionKey,
    shouldShowOffer: shouldShowOffer,
    shouldShowCartOffer: shouldShowCartOffer,
    gaParams: gaParams,
  };
});
